import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, RotateCcw } from 'lucide-react';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import {
  EvalKpi, FindingsPanel, Note, ScreenField, ScreenFilters,
} from '../../components/performance/evaluation/EvaluationParts';
import {
  formatNum, listOf, metaOfContext, metricByCode, useScreenParams,
} from '../../components/performance/evaluation/evaluationAnalytics';
import { useAuth } from '../../context/AuthContext';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import {
  anyStale, fetchPeriods, oldestCalculatedAt, usePanel,
} from '../performance-operations/config';
import ScatterPanel from './components/ScatterPanel';
import RaterProfilePanel from './components/RaterProfilePanel';
import AnonymousGroupsPanel from './components/AnonymousGroupsPanel';
import ReviewEmployees from './components/ReviewEmployees';
import {
  COVERAGES, DIRECTIONS, GAP_BANDS, NS, Q, RATER_GROUPS, SCREEN_KEYS, binFilters, fetchFindings, fetchSummary, screenFilters,
} from './config';

// PM Analitik P4 — E06 Değerlendirme Farkları ve 360 Sonuçları (/performance/review-gaps). İK ve yetkili yönetici (kendi kapsamı;
// sunucu kapısı PmAnalyticsScopeRules). M18 yönü sabit: yönetici − öz. 360 anonimliği: yanıtlayan adı/tekil puan/yanıt zamanı
// hiçbir panelde ve dışa aktarımda yok; anonim grup a eşiği altında puan ve profil noktası bastırılır. Süzgeçler URL'dedir;
// saçılım kovası → liste → E14 görüşme sekmesi.
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: -4px 0 16px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const staticLookup = (options) => ({
  onSearch: async (q) => options.filter((o) => !q || o.name.toLocaleLowerCase().includes(String(q).toLocaleLowerCase())),
  onGetById: async (id) => options.find((o) => o.id === id) || { id, name: '' },
});

export default function PerformanceReviewGapsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [values, setScreen] = useScreenParams(SCREEN_KEYS);
  const screen = useMemo(() => screenFilters(values), [values]);
  const bins = useMemo(() => binFilters(values), [values]);
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify([filters, screen]);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  const summary = usePanel(() => fetchSummary(filters, screen), [filterKey], ready);
  const findings = usePanel(() => fetchFindings(filters, screen), [filterKey], ready);
  useEffect(() => { if (summary.data) onMeta('summary', metaOfContext(summary.data)); }, [summary.data, onMeta]);
  const context = summary.data?.context;

  const competencyOptions = useMemo(() => (summary.data?.competencies || []).map((c) => ({ id: c.id, name: c.name })), [summary.data]);
  const directionLookup = useMemo(() => staticLookup(DIRECTIONS.map((x) => ({ id: x, name: t(`${NS}.direction.${x}`) }))), [t]);
  const bandLookup = useMemo(() => staticLookup(GAP_BANDS.map((x) => ({ id: x, name: t(`${NS}.gapBand.${x}`) }))), [t]);
  const coverageLookup = useMemo(() => staticLookup(COVERAGES.map((x) => ({ id: x, name: t(`${NS}.coverage.${x}`) }))), [t]);
  const groupLookup = useMemo(() => staticLookup(RATER_GROUPS.map((x) => ({ id: x, name: t(`${NS}.raterGroup.${x}`) }))), [t]);

  const scrollToList = () => {
    window.setTimeout(() => document.querySelector('[data-panel="employees"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const openBin = (b) => {
    setScreen({ [Q.selfBin]: b.self, [Q.managerBin]: b.manager });
    scrollToList();
  };

  const onFinding = (f) => {
    const af = f.actionFilter || {};
    const patch = {};
    if (af['gapBand[in]']) patch[Q.bands] = listOf(af['gapBand[in]']);
    if (af['coverage[in]']) patch[Q.coverages] = listOf(af['coverage[in]']);
    setScreen(patch);
    scrollToList();
  };

  const screenActive = SCREEN_KEYS.filter((k) => k !== Q.managerBin && values[k]);
  const competencyName = (id) => competencyOptions.find((c) => c.id === id)?.name || '';
  const chipText = (k) => {
    const list = listOf(values[k]);
    switch (k) {
      case Q.competency: return `${t(`${NS}.filter.competency`)}: ${competencyName(values[k])}`;
      case Q.directions: return `${t(`${NS}.filter.direction`)}: ${list.map((x) => t(`${NS}.direction.${x}`)).join(', ')}`;
      case Q.bands: return `${t(`${NS}.filter.gapBand`)}: ${list.map((x) => t(`${NS}.gapBand.${x}`)).join(', ')}`;
      case Q.coverages: return `${t(`${NS}.filter.coverage`)}: ${list.map((x) => t(`${NS}.coverage.${x}`)).join(', ')}`;
      case Q.groups: return `${t(`${NS}.filter.raterGroup`)}: ${list.map((x) => t(`${NS}.raterGroup.${x}`)).join(', ')}`;
      case Q.selfBin: return t(`${NS}.filter.bin`, { self: formatNum(values[Q.selfBin], lang), manager: formatNum(values[Q.managerBin], lang) });
      default: return k;
    }
  };
  const clearChip = (k) => setScreen(k === Q.selfBin ? { [Q.selfBin]: null, [Q.managerBin]: null } : { [k]: null });

  const m = (code) => metricByCode(summary.data?.metrics, code);
  const common = { ns: NS, loading: summary.loading, error: summary.error, onRetry: summary.retry };
  const s = summary.data;
  const directionSub = s && s.close !== null && s.close !== undefined
    ? t(`${NS}.kpi.directionSub`, {
      close: formatNum(s.close, lang, 0), managerHigher: formatNum(s.managerHigher, lang, 0), selfHigher: formatNum(s.selfHigher, lang, 0),
    })
    : undefined;
  const pairOnly = !!(values[Q.directions] || values[Q.bands] || values[Q.selfBin]);

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t(`${NS}.title`) }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <>
          <ScreenFilters data-filters="screen">
            <ScreenField $w="240px">
              <GenericSelect
                label={t(`${NS}.filter.competency`)} value={values[Q.competency] || null} options={competencyOptions} idKey="id" labelKey="name"
                placeholder={t(`${NS}.filter.allCompetencies`)} onChange={(v) => setScreen({ [Q.competency]: v || null })}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.direction`)} value={listOf(values[Q.directions])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.directions]: v || [] })} {...directionLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.gapBand`)} value={listOf(values[Q.bands])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.bands]: v || [] })} {...bandLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.coverage`)} value={listOf(values[Q.coverages])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.coverages]: v || [] })} {...coverageLookup}
              />
            </ScreenField>
            <ScreenField>
              <AsyncGenericSelect
                label={t(`${NS}.filter.raterGroup`)} value={listOf(values[Q.groups])} isMulti minChars={0} compact
                onChange={(v) => setScreen({ [Q.groups]: v || [] })} {...groupLookup}
              />
            </ScreenField>
          </ScreenFilters>
          {screenActive.length > 0 ? (
            <Chips>
              {screenActive.map((k) => (
                <Chip key={k} data-chip={k}>
                  {chipText(k)}
                  <Button variant="ghost" aria-label={t('performance.operations.filter.remove')} onClick={() => clearChip(k)}>
                    <X size={12} />
                  </Button>
                </Chip>
              ))}
              <Button variant="ghost" data-action="screen-reset" onClick={() => setScreen(Object.fromEntries(SCREEN_KEYS.map((k) => [k, null])))}>
                <RotateCcw size={14} />
                {' '}
                {t(`${NS}.filter.clear`)}
              </Button>
            </Chips>
          ) : null}
          <Note>{t(`${NS}.scaleNote`, { delta: formatNum(s?.delta ?? 0.5, lang), threshold: formatNum(s?.largeThreshold ?? 1, lang) })}</Note>
          <Grid key={filters.periodId}>
            <EvalKpi {...common} code="M18" title={t(`${NS}.kpi.M18`)} metric={m('M18')} signed sub={directionSub} />
            <EvalKpi {...common} code="M19" title={t(`${NS}.kpi.M19`)} metric={m('M19')} />
            <EvalKpi {...common} code="M20" title={t(`${NS}.kpi.M20`)} metric={m('M20')} />
            <EvalKpi {...common} code="M21" title={t(`${NS}.kpi.M21`)} metric={m('M21')} />
            {context && !context.systemBEnabled ? (
              <PageEmpty data-state="no-360" style={{ gridColumn: 'span 12' }}>{t(`${NS}.no360`)}</PageEmpty>
            ) : null}
            <ScatterPanel filters={filters} screen={screen} onOpen={openBin} onMeta={onMeta} />
            <RaterProfilePanel filters={filters} screen={screen} onMeta={onMeta} />
            <AnonymousGroupsPanel filters={filters} screen={screen} onMeta={onMeta} />
            <FindingsPanel ns={NS} panel={findings} span={7} onAction={onFinding} />
            <ReviewEmployees filters={filters} screen={screen} bins={bins} isHr={isHr} pairOnly={pairOnly} />
          </Grid>
        </>
      ) : null}
    </PageWrap>
  );
}
